import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { prescreenAdminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { AlertCircle, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, MessageCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';

const OUTCOME_COLORS = {
  DPIA_REQUIRED: {
    icon: AlertCircle,
    badge: 'destructive',
    bgClass: 'bg-destructive/10 border-destructive/30',
    textClass: 'text-destructive',
    label: 'DPIA Required',
  },
  DPIA_RECOMMENDED: {
    icon: AlertTriangle,
    badge: 'warning',
    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
    textClass: 'text-yellow-700 dark:text-yellow-500',
    label: 'DPIA Recommended',
  },
  NO_REVIEW_REQUIRED: {
    icon: CheckCircle2,
    badge: 'success',
    bgClass: 'bg-green-500/10 border-green-500/30',
    textClass: 'text-green-700 dark:text-green-500',
    label: 'No Review Required',
  },
  SPEAK_TO_DPO: {
    icon: MessageCircle,
    badge: 'secondary',
    bgClass: 'bg-blue-500/10 border-blue-500/30',
    textClass: 'text-blue-700 dark:text-blue-500',
    label: 'Speak to DPO',
  },
};

export default function PreScreenDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prescreen, setPrescreen] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideOutcome, setOverrideOutcome] = useState('');
  const [overrideRationale, setOverrideRationale] = useState('');
  const [overriding, setOverriding] = useState(false);

  useEffect(() => {
    loadPrescreen();
  }, [id]);

  const loadPrescreen = async () => {
    try {
      setLoading(true);
      const res = await prescreenAdminAPI.getOne(id);
      setPrescreen(res.data);
      setNotes(res.data.dpo_notes || '');
    } catch (err) {
      console.error('Error loading pre-screen:', err);
      toast.error('Failed to load pre-screen submission');
      navigate('/risk/assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      // Save notes - need to implement this endpoint if not already there
      // For now, just update local state
      toast.success('Notes saved');
    } catch (err) {
      console.error('Error saving notes:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideOutcome || !overrideRationale) {
      toast.error('Please select outcome and provide rationale');
      return;
    }

    try {
      setOverriding(true);
      await prescreenAdminAPI.overrideOutcome(id, overrideOutcome, overrideRationale);
      toast.success('Outcome overridden successfully');
      setOverrideOpen(false);
      setOverrideOutcome('');
      setOverrideRationale('');
      // Reload the prescreen data to reflect the new outcome
      await loadPrescreen();
    } catch (err) {
      console.error('Error overriding outcome:', err);
      toast.error('Failed to override outcome');
    } finally {
      setOverriding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!prescreen) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate('/risk/assessments')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="pt-6">
              <p className="text-destructive">Pre-screen submission not found</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const outcomeConfig = OUTCOME_COLORS[prescreen.outcome] || OUTCOME_COLORS.DPIA_RECOMMENDED;
  const OutcomeIcon = outcomeConfig.icon;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/risk/assessments')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Pre-Screen Review</h1>
              <p className="text-sm text-muted-foreground">
                {prescreen.prescreen_reference}
              </p>
            </div>
          </div>
        </div>

        {/* Outcome Card */}
        <Card className={`mb-6 border ${outcomeConfig.bgClass}`}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <OutcomeIcon className={`w-6 h-6 ${outcomeConfig.textClass}`} />
                <div>
                  <CardTitle className={outcomeConfig.textClass}>
                    {outcomeConfig.label}
                  </CardTitle>
                  <Badge variant={outcomeConfig.badge} className="mt-2">
                    {prescreen.outcome}
                  </Badge>
                </div>
              </div>
              {!isComplianceReadOnly && (
                <Button
                  variant="outline"
                  onClick={() => setOverrideOpen(true)}
                  className="text-xs"
                >
                  Override
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium text-foreground mb-2">Rationale:</p>
            <p className="text-sm text-muted-foreground">{prescreen.outcome_rationale}</p>
            {prescreen.intake_reference && (
              <div className="mt-4 p-3 bg-muted/50 rounded border border-primary/20">
                <p className="text-xs font-medium text-foreground mb-1">Auto-Created DPIA:</p>
                <Badge variant="secondary">{prescreen.intake_reference}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submitter Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Submitter Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Name</p>
                <p className="text-sm font-medium text-foreground">{prescreen.submitter_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Email</p>
                <p className="text-sm font-medium text-foreground">{prescreen.submitter_email}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Title</p>
                <p className="text-sm font-medium text-foreground">{prescreen.submitter_title || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Department</p>
                <p className="text-sm font-medium text-foreground">{prescreen.submitter_department || '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase">Submitted</p>
              <p className="text-sm text-foreground">
                {new Date(prescreen.created_at).toLocaleDateString()} at{' '}
                {new Date(prescreen.created_at).toLocaleTimeString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Assessment Answers */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Assessment Answers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {prescreen.q1_processes_personal_data !== undefined && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q1: Does your activity involve processing personal data?</p>
                <Badge variant={prescreen.q1_processes_personal_data ? 'default' : 'secondary'}>
                  {prescreen.q1_processes_personal_data ? 'Yes' : 'No'}
                </Badge>
              </div>
            )}
            {prescreen.q2_processing_necessary && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q2: Is processing this data necessary for your project objectives?</p>
                <Badge variant="outline">{prescreen.q2_processing_necessary.replace('_', ' ')}</Badge>
              </div>
            )}
            {prescreen.q3_subject_types && prescreen.q3_subject_types.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q3: Categories of data subjects</p>
                <div className="flex flex-wrap gap-2">
                  {prescreen.q3_subject_types.map(type => (
                    <Badge key={type} variant="secondary" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {prescreen.q4_data_types && prescreen.q4_data_types.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q4: Categories of data processed</p>
                <div className="flex flex-wrap gap-2">
                  {prescreen.q4_data_types.map(cat => (
                    <Badge key={cat} variant="secondary" className="text-xs">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {prescreen.q5_volume && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q5: Volume of data subjects</p>
                <Badge variant="outline">{prescreen.q5_volume}</Badge>
              </div>
            )}
            {prescreen.q6_external_sharing !== undefined && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q6: Data shared externally?</p>
                <Badge variant={prescreen.q6_external_sharing ? 'destructive' : 'success'}>
                  {prescreen.q6_external_sharing ? 'Yes' : 'No'}
                </Badge>
                {prescreen.q6_external_description && (
                  <p className="text-xs text-muted-foreground mt-2">{prescreen.q6_external_description}</p>
                )}
              </div>
            )}
            {prescreen.q7_monitoring !== undefined && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q7: Systematic monitoring?</p>
                <Badge variant={prescreen.q7_monitoring ? 'destructive' : 'success'}>
                  {prescreen.q7_monitoring ? 'Yes' : 'No'}
                </Badge>
                {prescreen.q7_monitoring_description && (
                  <p className="text-xs text-muted-foreground mt-2">{prescreen.q7_monitoring_description}</p>
                )}
              </div>
            )}
            {prescreen.q8_ai_systems !== undefined && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Q8: AI/Automated decision-making?</p>
                <Badge variant={prescreen.q8_ai_systems ? 'destructive' : 'success'}>
                  {prescreen.q8_ai_systems ? 'Yes' : 'No'}
                </Badge>
                {prescreen.q8_ai_description && (
                  <p className="text-xs text-muted-foreground mt-2">{prescreen.q8_ai_description}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* DPO Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">DPO Review Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Add your review notes here..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-24 mb-4"
            />
            <Button onClick={handleSaveNotes} disabled={saving} className="w-full sm:w-auto">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Notes'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Override Outcome Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override Assessment Outcome</DialogTitle>
            <DialogDescription>
              Change the assessment outcome and provide a reason for the override.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">New Outcome</label>
              <select
                value={overrideOutcome}
                onChange={(e) => setOverrideOutcome(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              >
                <option value="">Select outcome...</option>
                <option value="DPIA_REQUIRED">DPIA Required</option>
                <option value="DPIA_RECOMMENDED">DPIA Recommended</option>
                <option value="SPEAK_TO_DPO">Speak to DPO</option>
                <option value="NO_REVIEW_REQUIRED">No Review Required</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Reason for Override</label>
              <Textarea
                placeholder="Explain why you are overriding the automated outcome..."
                value={overrideRationale}
                onChange={(e) => setOverrideRationale(e.target.value)}
                className="min-h-20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleOverride} disabled={overriding}>
              {overriding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Overriding...
                </>
              ) : (
                'Override'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
